// Copyright (C) 2026, Lifefinder Systems International AB

import React, { useEffect, useRef, useState } from 'react';
import { Circle, Polygon, useMap, useMapEvents } from 'react-leaflet';

import type { AreaShape, LatLng } from '../../../types';

import { buildCircleShape, buildPolygonShape, buildRectangleShape } from './shapeBuilders';

export interface DrawingLayerProps {
  drawMode: 'circle' | 'rectangle' | 'polygon';
  onShapeComplete: (shape: AreaShape) => void;
}

const PREVIEW_OPTIONS = {
  color: '#f90',
  fillColor: '#f90',
  fillOpacity: 0.15,
  dashArray: '6 4',
  weight: 2,
};

const DrawingLayer: React.FC<DrawingLayerProps> = ({ drawMode, onShapeComplete }) => {
  const map = useMap();

  // For circle and rectangle: first click sets anchor, second click completes
  const [anchor, setAnchor] = useState<LatLng | null>(null);
  // For polygon: accumulate clicks
  const [polyVerts, setPolyVerts] = useState<LatLng[]>([]);
  // Live cursor position for preview while moving
  const [cursor, setCursor] = useState<LatLng | null>(null);

  // Keep a ref to drawMode so event handlers inside useMapEvents always see the latest value
  const drawModeRef = useRef(drawMode);
  useEffect(() => {
    drawModeRef.current = drawMode;
    // Reset partial state when mode switches
    setAnchor(null);
    setPolyVerts([]);
    setCursor(null);
  }, [drawMode]);

  useEffect(() => {
    map.getContainer().style.cursor = 'crosshair';
    return () => {
      map.getContainer().style.cursor = '';
    };
  }, [map]);

  useMapEvents({
    mousemove(e) {
      setCursor([e.latlng.lat, e.latlng.lng]);
    },
    click(e) {
      const pt: LatLng = [e.latlng.lat, e.latlng.lng];
      const mode = drawModeRef.current;

      if (mode === 'circle' || mode === 'rectangle') {
        if (!anchor) {
          setAnchor(pt);
        } else {
          const shape =
            mode === 'circle'
              ? buildCircleShape(anchor, map.distance(anchor, pt))
              : buildRectangleShape(anchor, pt);
          setAnchor(null);
          setCursor(null);
          onShapeComplete(shape);
        }
      } else {
        // polygon — single clicks add vertices
        setPolyVerts((prev) => [...prev, pt]);
      }
    },
    dblclick(e) {
      if (drawModeRef.current !== 'polygon') return;
      // Prevent the map from zooming on double-click while drawing
      e.originalEvent.preventDefault();
      map.doubleClickZoom.disable();
      setTimeout(() => map.doubleClickZoom.enable(), 300);

      setPolyVerts((prev) => {
        if (prev.length < 3) return prev;
        const shape = buildPolygonShape(prev);
        onShapeComplete(shape);
        return [];
      });
      setCursor(null);
    },
  });

  // --- Preview rendering ---

  if (drawMode === 'circle' && anchor && cursor) {
    const radius = map.distance(anchor, cursor);
    return <Circle center={anchor} radius={radius} pathOptions={PREVIEW_OPTIONS} />;
  }

  if (drawMode === 'rectangle' && anchor && cursor) {
    const [lat1, lng1] = anchor;
    const [lat2, lng2] = cursor;
    const corners: LatLng[] = [
      [lat1, lng1],
      [lat1, lng2],
      [lat2, lng2],
      [lat2, lng1],
    ];
    return <Polygon positions={corners} pathOptions={PREVIEW_OPTIONS} />;
  }

  if (drawMode === 'polygon' && polyVerts.length > 0 && cursor) {
    const preview = [...polyVerts, cursor];
    return <Polygon positions={preview} pathOptions={PREVIEW_OPTIONS} />;
  }

  return null;
};

export default DrawingLayer;
